import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  Typography
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterSelectInput from 'src/common/FilterSelectInput';
import DateFilterField from 'src/common/formFields/DateFilterField';
import {
  TRANSACTION_STYLE_COLORS,
  TRANSACTION_DATE_PICKER_POPPER_SX,
  TRANSACTION_SELECT_MENU_PROPS
} from './transactionStyleTokens';

const transactionDateTimeFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: TRANSACTION_STYLE_COLORS.white,
    color: TRANSACTION_STYLE_COLORS.foreground,
    '& fieldset': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` },
    '&:hover fieldset': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` },
    '&.Mui-focused fieldset': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` }
  },
  '& .MuiInputLabel-root': {
    color: TRANSACTION_STYLE_COLORS.primary,
    '&.Mui-focused': { color: TRANSACTION_STYLE_COLORS.primary }
  },
  '& .MuiSvgIcon-root, & .MuiIconButton-root': { color: TRANSACTION_STYLE_COLORS.primary },
  '& .MuiIconButton-root': {
    '&:hover': { backgroundColor: TRANSACTION_STYLE_COLORS.hover }
  },
  width: '100%',
  minWidth: 0,
  maxWidth: '100%',
  '& .MuiOutlinedInput-input': {
    fontSize: '0.9375rem',
    paddingRight: '0 !important'
  },
  '& .MuiInputAdornment-positionEnd': {
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'flex-end',
    gap: 0.75,
    ml: 0.75,
    mr: 0.5
  },
  '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
    flexShrink: 0
  }
};

const TransactionFilters = ({
  anchorEl,
  onClose,
  onApply,
  onClear,
  isIssued,
  isInward,
  siteOptions,
  tankOptions,
  draftStartDateTime,
  setDraftStartDateTime,
  draftEndDateTime,
  setDraftEndDateTime,
  draftSite,
  setDraftSite,
  draftTank,
  setDraftTank,
  filterError
}) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    PaperProps={{
      sx: {
        width: { xs: 'calc(100vw - 24px)', sm: 560 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'min(560px, calc(100vh - 96px))',
        overflow: 'auto',
        mt: 1,
        p: { xs: 2, sm: 2.5 },
        boxSizing: 'border-box',
        borderRadius: 2,
        border: '1px solid',
        borderColor: TRANSACTION_STYLE_COLORS.primary,
        boxShadow: '0 20px 25px -5px rgb(15 23 42 / 0.1), 0 8px 10px -6px rgb(15 23 42 / 0.1)',
        backgroundColor: TRANSACTION_STYLE_COLORS.white,
        color: TRANSACTION_STYLE_COLORS.foreground,
        '& .MuiDivider-root': { borderColor: TRANSACTION_STYLE_COLORS.primary },
        '& .MuiFormControl-root': { backgroundColor: TRANSACTION_STYLE_COLORS.white },
        '& .MuiOutlinedInput-root': {
          backgroundColor: TRANSACTION_STYLE_COLORS.white,
          color: TRANSACTION_STYLE_COLORS.foreground,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: `${TRANSACTION_STYLE_COLORS.primary} !important` }
        },
        '& .MuiInputLabel-root': {
          color: TRANSACTION_STYLE_COLORS.primary,
          '&.Mui-focused': { color: TRANSACTION_STYLE_COLORS.primary }
        },
        '& .MuiSelect-icon, & .MuiSvgIcon-root': { color: TRANSACTION_STYLE_COLORS.primary },
        '& .MuiMenuItem-root.Mui-selected': {
          backgroundColor: `${TRANSACTION_STYLE_COLORS.selected} !important`,
          color: TRANSACTION_STYLE_COLORS.foreground
        },
        '& .MuiMenuItem-root:hover': { backgroundColor: TRANSACTION_STYLE_COLORS.hover }
      }
    }}
  >
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <FilterAltOutlinedIcon sx={{ color: TRANSACTION_STYLE_COLORS.primary }} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" fontWeight={700}>Transaction filters</Typography>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <FilterSelectInput
          label="Site"
          name="transaction_site_filter"
          value={draftSite}
          data={siteOptions}
          onInputChange={setDraftSite}
          menuProps={TRANSACTION_SELECT_MENU_PROPS}
          sx={{ width: '100%', mr: 0 }}
        />
        {(isIssued || isInward) && (
          <FilterSelectInput
            label="Tank"
            name="transaction_tank_filter"
            value={draftTank}
            data={tankOptions}
            onInputChange={setDraftTank}
            menuProps={TRANSACTION_SELECT_MENU_PROPS}
            sx={{ width: '100%', mr: 0 }}
          />
        )}
        <DateFilterField
          label="Start Date Time *"
          pickerPopperSx={TRANSACTION_DATE_PICKER_POPPER_SX}
          shrinkLabel
          value={draftStartDateTime}
          onlyDate={false}
          onDateChange={(value) => setDraftStartDateTime(value)}
          onClearFilter={() => setDraftStartDateTime(null)}
          pickerProps={{
            maxDateTime: draftEndDateTime || undefined
          }}
          sx={transactionDateTimeFieldSx}
        />
        <DateFilterField
          label="End Date Time *"
          pickerPopperSx={TRANSACTION_DATE_PICKER_POPPER_SX}
          shrinkLabel
          value={draftEndDateTime}
          onlyDate={false}
          onDateChange={(value) => setDraftEndDateTime(value)}
          onClearFilter={() => setDraftEndDateTime(null)}
          pickerProps={{ minDateTime: draftStartDateTime || undefined }}
          sx={transactionDateTimeFieldSx}
        />
        {filterError && (
          <Typography variant="body2" color="error" sx={{ gridColumn: '1 / -1' }}>
            {filterError}
          </Typography>
        )}
      </Box>
      <Divider />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={onClear}
          sx={{
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: 'hsl(198 87% 32%)',
            color: 'hsl(198 87% 32%)',
            '&:hover': {
              borderColor: 'hsl(198 87% 32%)',
              color: 'hsl(198 87% 32%)',
              backgroundColor: 'rgba(11, 106, 153, 0.06)'
            }
          }}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          onClick={onApply}
          sx={{
            borderRadius: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            color: '#ffffff',
            backgroundColor: 'hsl(198 87% 32%)',
            borderColor: 'hsl(198 87% 32%)',
            boxShadow: '0 1px 2px rgb(15 23 42 / 0.05)',
            '&:hover': {
              backgroundColor: 'hsl(198 87% 32%)',
              borderColor: 'hsl(198 87% 32%)',
              filter: 'brightness(0.95)'
            },
            '&:active': { filter: 'brightness(0.9)' }
          }}
        >
          Apply
        </Button>
      </Stack>
    </Stack>
  </Popover>
);

TransactionFilters.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isIssued: PropTypes.bool.isRequired,
  isInward: PropTypes.bool.isRequired,
  siteOptions: PropTypes.array.isRequired,
  tankOptions: PropTypes.array.isRequired,
  draftStartDateTime: PropTypes.any,
  setDraftStartDateTime: PropTypes.func.isRequired,
  draftEndDateTime: PropTypes.any,
  setDraftEndDateTime: PropTypes.func.isRequired,
  draftSite: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setDraftSite: PropTypes.func.isRequired,
  draftTank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  setDraftTank: PropTypes.func.isRequired,
  filterError: PropTypes.string
};

TransactionFilters.defaultProps = {
  draftStartDateTime: null,
  draftEndDateTime: null,
  filterError: ''
};

export default TransactionFilters;
